import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'

import AvatarCircle from './AvatarCircle.jsx'
import { PosterSlot } from './WatchPanel.jsx'
import { MemberLink } from './Person.jsx'
import WatchScreenTabs from './WatchScreenTabs.jsx'
import {
  MISCONFIGURED as ARCHIVE_MISCONFIGURED,
  READY as ARCHIVE_READY,
  UNAVAILABLE as ARCHIVE_UNAVAILABLE,
  titlesOf,
  useWatchArchive,
} from './watchArchive.js'
import { groupLoved, topThree } from './watchGoated.js'
import { hueForTitle, memberProfilePath } from './watch.js'
import { LOADING as SESSION_LOADING, SIGNED_IN, signInHref, useSession } from '../session.js'
import { UNKNOWN_NAME, nameFor, useDirectory } from '../directory.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The Goated screen — HANDOFF §6 ("start" in the prototype), the fourth
// Watch screen tab. V2-8c.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 SAME GATING SHAPE AS EVERY SIBLING SCREEN, ON PURPOSE — ONE ARCHIVE
//    FETCH, NOT A THIRD/FOURTH ENDPOINT
// ═══════════════════════════════════════════════════════════════════════════
//
// "The group loved" and "Everyone's top three" are both derived from
// `3a/watch-archive.json`'s own `titles[]` — the SAME archive `WatchArchive.
// jsx` and `WatchLeaderboards.jsx` already fetch through `watchArchive.js::
// useWatchArchive`. This page reuses that hook rather than inventing a new
// one: signed out gets the identical door, a misconfigured deployment gets
// the identical sentence, and a genuinely unavailable archive gets the
// identical retry copy. Unlike `WatchLeaderboards.jsx`, there is no separate
// "not published yet" branch here — `titlesOf` already returns `[]` for an
// archive with no titles at all (an empty, not a missing, key), and an empty
// `titles[]` is exactly what produces this page's own two honest empty
// lines below, never a third "nothing to see" state layered on top.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 "TODAY, THIS IS THE LIKELY REAL STATE" (brief §2) — AN EMPTY "THE GROUP
//    LOVED" SECTION WITH A REAL, SIGNED-IN SESSION IS CORRECT, NOT A BUG
// ═══════════════════════════════════════════════════════════════════════════
//
// With one member's records in the store, no title can have 3 finishers
// yet. `groupLoved` returning `[]` is the honest answer, drawn as one plain
// line (brief §2: "Honest empty states, one plain line each and never an
// empty box") — never a skeleton, never "coming soon."

export default function WatchGoated() {
  useDocumentTitle('Goated')
  const location = useLocation()
  const session = useSession()
  const signedIn = session.status === SIGNED_IN
  const archive = useWatchArchive(signedIn)
  const directory = useDirectory()

  if (session.status === SESSION_LOADING) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="goated-loading-session">
          Checking whether you are signed in.
        </p>
      </Frame>
    )
  }

  if (!signedIn) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="goated-signed-out">
          You need to be signed in to see this. <a href={signInHref(location)}>Sign in with Discord</a>.
        </p>
      </Frame>
    )
  }

  if (archive.state === ARCHIVE_MISCONFIGURED) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="goated-misconfigured">
          The archive is set up wrong on this deployment, so it cannot be shown until that is
          fixed — this is not about your account.
        </p>
      </Frame>
    )
  }

  if (archive.state !== ARCHIVE_READY) {
    return (
      <Frame>
        <p
          className="d2d-notice"
          data-testid={archive.state === ARCHIVE_UNAVAILABLE ? 'goated-unavailable' : 'goated-loading'}
        >
          {archive.state === ARCHIVE_UNAVAILABLE
            ? 'The archive is unavailable right now. Try again in a minute.'
            : 'Loading the archive…'}
        </p>
      </Frame>
    )
  }

  return (
    <Frame>
      <GoatedBody shared={archive.shared} directory={directory} viewerUserId={session.userId} />
    </Frame>
  )
}

function Frame({ children }) {
  return (
    <article className="d2d-page d2d-watch d2d-goated" data-testid="watch-goated-page">
      <WatchScreenTabs active="goated" />
      <div className="d2d-goated__head">
        <div className="d2d-micro d2d-goated__kicker">New here?</div>
        <h1 className="d2d-goated__h1">Goated</h1>
        {
          // HANDOFF §6 / prototype `isStart` block, verbatim (brief §1 —
          // the design's own straight apostrophe, not a typographic one).
        }
        <p className="d2d-goated__lede">
          What the group loved most, and everyone's top three. Tap any title to see where people
          are and what they said.
        </p>
      </div>
      {children}
    </article>
  )
}

function GoatedBody({ shared, directory, viewerUserId }) {
  const titles = useMemo(() => titlesOf(shared), [shared])
  const loved = useMemo(() => groupLoved(titles), [titles])
  const tops = useMemo(() => topThree(titles), [titles])

  return (
    <div className="d2d-goated__body">
      <section className="d2d-watch__section" aria-label="The group loved" data-testid="goated-loved-section">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">The group loved</span>
        {loved.length === 0 ? (
          <p className="d2d-watch__flatline" data-testid="goated-loved-empty">
            Nothing three members have finished yet.
          </p>
        ) : (
          <div className="d2d-goated__lovedgrid" data-testid="goated-loved-grid">
            {loved.map((item) => (
              <LovedTile key={item.title.id} item={item} />
            ))}
          </div>
        )}
      </section>

      <section className="d2d-watch__section" aria-label="Everyone's top three" data-testid="goated-tops-section">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Everyone's top three</span>
        {tops.length === 0 ? (
          <p className="d2d-watch__flatline" data-testid="goated-tops-empty">
            Nobody has picked favourites yet.
          </p>
        ) : (
          <div className="d2d-goated__topsgrid" data-testid="goated-tops-grid">
            {tops.map((member) => (
              <TopsCard
                key={member.memberId}
                member={member}
                directory={directory}
                viewerUserId={viewerUserId}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

/** One poster tile — brief §2: "titles with 3+ members finished... sorted
 * by average rating," each linking to its title page (brief §2.2: "every
 * title links to its title page"). `PosterSlot`'s own `size="goated"` has
 * no rule in the shared ladder (`app.css`) — this page's own stylesheet
 * adds it, deliberately not `app.css` (see `watch-goated.css`'s own header
 * on why: this lane does not own that file). */
function LovedTile({ item }) {
  const hue = hueForTitle(item.title)
  return (
    <Link
      className="d2d-goated__lovedtile"
      to={`/watch/${item.title.id}`}
      data-testid="goated-loved-tile"
      style={{ '--d2d-title-hue': hue }}
    >
      <PosterSlot subscription={item.title} size="goated" />
      <span className="d2d-goated__lovedtitle">{item.title.title}</span>
      <span className="d2d-micro d2d-goated__lovedstat" data-testid="goated-loved-stat">
        {item.stat}
      </span>
    </Link>
  )
}

/** One member's card — HANDOFF §6: "a 40 px profile picture (orange ring
 * and name for you) and a ranked list." The avatar stays a plain, `aria-
 * hidden` decoration next to the real link — `WatchLeaderboards.jsx::
 * BoardRow`'s own split (name is what a member actually taps), not the
 * prototype's own single combined button. Every favourite title links to
 * its own title page (brief §2.2), the same `/watch/:id` shape every other
 * screen in this feature already uses. */
function TopsCard({ member, directory, viewerUserId }) {
  const isYou = member.memberId === viewerUserId
  const person = nameFor(directory, member.memberId)
  const initial = person.known ? person.label.trim().charAt(0).toUpperCase() : '·'
  const to = memberProfilePath(member.memberId)

  return (
    <div className="d2d-goated__topscard" data-testid="goated-tops-card">
      <div className="d2d-goated__topshead">
        <AvatarCircle
          person={person}
          initial={initial}
          className={`d2d-avatar d2d-avatar--40 d2d-goated__avatar${isYou ? ' d2d-goated__avatar--you' : ''}`}
          testId="goated-tops-avatar"
        />
        <MemberLink
          to={to}
          className={`d2d-goated__name${isYou ? ' d2d-goated__name--you' : ''}`}
          testId="goated-tops-name"
        >
          {person.known ? person.label : UNKNOWN_NAME}
        </MemberLink>
      </div>
      <ol className="d2d-goated__favlist" data-testid="goated-tops-favlist">
        {member.favorites.map((favorite) => (
          <li key={favorite.title.id} className="d2d-goated__favrow" data-testid="goated-tops-fav-row">
            <span className="d2d-goated__favrank" aria-hidden="true">
              {favorite.rank}
            </span>
            <Link
              className="d2d-goated__favtitle"
              to={`/watch/${favorite.title.id}`}
              data-testid="goated-tops-fav-link"
            >
              {favorite.title.title}
            </Link>
          </li>
        ))}
      </ol>
    </div>
  )
}
